import React, { useEffect, useState } from "react";
import {
  FaUser,
  FaClock,
  FaCarAlt,
  FaPlusCircle,
  FaWallet,
  FaIdCard,
  FaFile,
} from "react-icons/fa";
import Loader from "../Loader/Loader";
import useApiLoader from "../../Utils/useApiLoader";
import api from "../../setupMiddleware";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment";
import { Link } from "react-router-dom";

const AdminDashboard = () => {
  const [summary, setSummary] = useState({
    total_users: 0,
    rc_requests: 0,
    dl_requests: 0,
    form24_requests: 0,
  });

  const [transactions, setTransactions] = useState([]);

  const loading = useApiLoader();

  const getSummary = async () => {
    await api
      .get("/api/admin/summary")
      .then((res) => {
        if (res && res.status === 200) {
          setSummary(res.data?.summary);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const fetchTransaction = async () => {
    try {
      const res = await api.get("/api/admin/transactions");
      if (res.status === 200) {
        setTransactions(res.data?.transactions || []);
      }
    } catch (err) {
      console.error("Error fetching transactions", err);
    }
  };

  useEffect(() => {
    fetchTransaction();
  }, []);

  const cards = [
    {
      title: "Total Users",
      value: summary?.total_users,
      icon: <FaUser />,
      color: "text-brand",
    },
    {
      title: "RC Requests",
      value: summary?.rc_requests,
      icon: <FaIdCard />,
      color: "text-brand",
    },
    {
      title: `DL Requests`,
      value: summary?.dl_requests,
      icon: <FaIdCard />,
      color: "text-brand",
    },
    {
      title: `Form 24 Requests`,
      value: summary?.form24_requests,
      icon: <FaFile />,
      color: "text-brand",
    },
  ];

  useEffect(() => {
    getSummary();
  }, []);
  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {loading && <Loader opacity={50} />}
      {/* Top Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {cards.map((card, index) => (
          <div
            key={index}
            className="bg-white rounded-2xl shadow-sm p-5 flex justify-between items-center border border-gray-100"
          >
            <div>
              <h3 className="text-sm font-medium text-brand mb-2">
                {card.title}
              </h3>
              <p className="text-2xl font-semibold text-gray-800">
                {card.value}
              </p>
            </div>
            <div className="p-3 bg-rose-50 rounded-lg text-2xl text-brand">
              {card.icon}
            </div>
          </div>
        ))}
      </div>

      {/* Recent Transactions */}
      <div className="bg-white/95 border border-gray-100 rounded-2xl shadow-[0_2px_6px_rgba(0,0,0,0.06)] mt-6 pb-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand/10 text-brand shadow-inner">
              <FaWallet className="w-4 h-4" />
            </div>
            <h2 className="font-semibold text-gray-800 tracking-wide">
              Recent Transactions
            </h2>
          </div>
          <Link
            to={"/transactions"}
            className="text-sm text-brand font-medium hover:underline"
          >
            View All
          </Link>
        </div>

        {/* List */}
        <div className="p-5 divide-y divide-gray-100">
          <AnimatePresence mode="wait">
            {transactions.length > 0 ? (
              transactions.slice(0, 5).map((tx) => (
                <motion.div
                  key={tx.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                  className="flex flex-row items-center justify-between gap-3 py-3 px-2 hover:bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-9 h-9 flex items-center justify-center rounded-xl ${
                        tx.type === "credit"
                          ? "bg-green-100 text-green-600"
                          : "bg-rose-100 text-rose-600"
                      } shadow-inner`}
                    >
                      {tx.type === "credit" ? (
                        <FaPlusCircle className="w-5 h-5" />
                      ) : (
                        <FaCarAlt className="w-5 h-5" />
                      )}
                    </div>

                    <div>
                      <p className="font-medium text-gray-800 text-sm">
                        {tx.type === "debit"
                          ? `${tx.request_type} ${tx.transaction_name}`
                          : tx.transaction_name}
                      </p>

                      <p className="text-[11px] text-gray-600">
                        {tx.user_name} <br />
                        <span className="text-gray-500">{tx.email}</span>
                      </p>

                      <p className="text-[11px] text-gray-400">
                        {moment(tx.transaction_time).format(
                          "DD MMM YYYY, hh:mm A"
                        )}
                      </p>
                    </div>
                  </div>

                  <span
                    className={`font-semibold text-xs md:text-sm px-3 py-1.5 rounded-full text-right ${
                      tx.type === "credit"
                        ? "text-green-700 bg-green-50"
                        : "text-rose-600 bg-rose-50"
                    }`}
                  >
                    {tx.type === "credit"
                      ? `+ ₹${Number(tx.amount).toLocaleString("en-IN")}`
                      : `- ₹${Math.abs(tx.amount).toLocaleString("en-IN")}`}
                  </span>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-5 text-gray-400 text-sm">
                No transactions found
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
